import { Inject, Injectable, OnDestroy } from '@angular/core';
import { LogInstanceToken, LogInterface, PaceInsightsService } from '@evorait/pace-logging';
import { TranslateService } from '@ngx-translate/core';
import { BsModalService } from 'ngx-bootstrap/modal';
import { from, Observable, of, Subject, throwError } from 'rxjs';
import { catchError, map, mergeMap, take, takeUntil, tap } from 'rxjs/operators';
import { UserMasterComponent } from '../../components/user-master/user-master.component';
import { UserMasterModel } from '../../components/user-master/user-master.model';
import { ENDPOINTS } from '../../enums/endpoints.enum';
import {
    ClientManagerInstanceToken,
    ClientManagerInterface,
    SharpServiceNames,
} from '../../interfaces/client-manager.interface';
import { InitializableInterface } from '../../interfaces/initializable.interface';
import { UserMasterInterface } from '../../interfaces/user-master.interface';
import { CompositeModel } from '../../models/complex/composite.model';
import { ODataUserModel, UserModel } from '../../models/complex/user.model';
import { TextModel } from '../../models/primitive/text.model';
import { User } from '../../types/user';
import { AppNotificationType } from '../app-notification/app-notification.model';
import { AppNotificationService } from '../app-notification/app-notification.service';
import { EventService } from '../event/event.service';
import { LocalStorageAppStateService } from '../local-storage/local-storage-app-state.service';
import { OnlineOnlyService } from '../online-only/online-only.service';
import { UriUtilitiesService } from '../uri-utilities/uri-utilities.service';
import { InitError } from '../base-initialization/init-error';
import { EnvironmentService } from '@evorait/pace-environment';
import { PacePartnerZgidErrorModalComponent } from '../../components/pace-partner-zgid-error-modal/pace-partner-zgid-error-modal.component';
import { ModalClass } from '../../enums/modal-bootstrap-classes.enum';

@Injectable()
export class UserMasterService implements InitializableInterface, UserMasterInterface, OnDestroy {
    private readonly url = ENDPOINTS.UserMasterSet;

    private userModels: UserModel[];
    private selectedUserModel: UserModel | undefined;
    private userRole: string;
    public isInitialized = false;
    public static readonly USERMODEL_NOT_SELECTED_MESSAGE = `User model not selected yet`;
    private destroyService = new Subject<void>();
    public defaultSharpServiceText = SharpServiceNames.PACE_PWA;

    public constructor(
        private readonly appNotificationService: AppNotificationService,
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly onlineOnlyService: OnlineOnlyService,
        private readonly localStorageAppStateService: LocalStorageAppStateService,
        private readonly modalService: BsModalService,
        private readonly eventService: EventService,
        @Inject(ClientManagerInstanceToken) private readonly clientManager: ClientManagerInterface,
        private readonly environmentService: EnvironmentService,
        private readonly translate: TranslateService,
        private readonly uriUtilities: UriUtilitiesService,
        private readonly paceInsights: PaceInsightsService,
    ) {}

    public ngOnDestroy(): void {
        this.destroyService.next();
        this.destroyService.complete();
    }

    public init(): Observable<void> {
        this.eventService.encryptionInitialized$.pipe(takeUntil(this.destroyService)).subscribe(() => {
            this.setUserModel();
        });

        // if the user already select a sales org/plant combination, we just re-use it
        if (this.selectedUserModel) {
            this.paceInsights
                .setUser(
                    this.selectedUserModel?.getFrontId(),
                    this.selectedUserModel?.getCountry(),
                    this.selectedUserModel?.getSalesOrg(),
                    this.selectedUserModel?.getPlant(),
                )
                .catch(console.error);
            this.userModels = [this.selectedUserModel];
            this.isInitialized = true;
            return of(undefined);
        } else {
            return this.getUserMasterData().pipe(
                mergeMap((userModels) => {
                    this.log.debug(
                        `UserMasterService: init: loaded ${userModels.length} user models: ${JSON.stringify(
                            userModels,
                        )})`,
                    );

                    this.userModels = userModels;

                    // Checks if there is only one sales org & plant combination available for an user.
                    // If yes, then its set the combination as default without showing the popup screen
                    // otherwise initialize the UI component and let the user to choose one from it.
                    if (this.userModels.length === 1) {
                        return of(userModels[0]);
                    } else {
                        // initialize UI component
                        const modalInstance = this.modalService.show(UserMasterComponent, {
                            backdrop: 'static',
                            class: 'modal-md',
                            initialState: {
                                model: new UserMasterModel(this.userModels),
                            },
                            ariaLabelledBy: 'user-master-modal-title',
                        });

                        if (!modalInstance.content) {
                            return throwError(
                                () => new InitError(new Error('No distinct sales org & plant selected'), true),
                            );
                        }

                        return modalInstance.content.selectedUserModel.asObservable();
                    }
                }),
                mergeMap((userModel) => {
                    this.paceInsights
                        .setUser(
                            userModel?.getFrontId(),
                            userModel?.getCountry(),
                            userModel?.getSalesOrg(),
                            userModel?.getPlant(),
                        )
                        .catch(console.error);
                    if (
                        userModel.getCountry().length === 0 ||
                        userModel.getPlant().length === 0 ||
                        userModel.getSalesOrg().length === 0
                    ) {
                        this.log.error(
                            `UserMasterService: init: the selected user model '${JSON.stringify(
                                userModel,
                            )}' does not contain all the required fields`,
                        );
                        this.appNotificationService.setAppNotification(
                            AppNotificationType.ERROR,
                            <string>this.translate.instant('USER_MASTER_SET_MISSING_DATA_TITLE'),
                            <string>this.translate.instant('USER_MASTER_SET_MISSING_DATA_BODY'),
                        );
                    }
                    return this.setSelectedUserModel(userModel);
                }),
                map((userModel) => {
                    this.log.debug(`UserMasterService: init: set selected user model '${JSON.stringify(userModel)}'`);
                    this.isInitialized = true;

                    // For the FI app we are using auth0 for internal and external users
                    if (
                        !this.clientManager.isPaceFormsFI() &&
                        this.environmentService.auth0Enabled &&
                        !this.isPaceOnlineUser()
                    ) {
                        // this is pace for partners, but the user is not a partner but an internal: we need show an error
                        this.modalService.show(PacePartnerZgidErrorModalComponent, {
                            backdrop: 'static',
                            class: ModalClass.MODAL_MEDIUM,
                            ariaLabelledBy: 'modal-title',
                        });
                        throw new InitError(
                            new Error('PACE for Partners is not accessible with an internal user account'),
                            true,
                        );
                    }
                }),
            );
        }
    }

    /**
     * This function sets the selected UserModel into the local storage
     *
     * @param userModel The selected UserModel
     * @returns Observable of the selected UserModel
     */
    public setSelectedUserModel(userModel: UserModel): Observable<UserModel> {
        // This is a hack.
        // We want to make the ClientManager to be able to perform more sophisticated checks like checking for AA and the country in one step.
        // The country comes from the userMasterService. As ClientManager is mostly used in a static way, we need to get the country value in,
        // so that the country can be stored and re-used for above mentioned checks.
        this.clientManager.userCountry = userModel.getCountry();

        this.selectedUserModel = userModel;
        return from(this.localStorageAppStateService.setUserData(userModel)).pipe(map(() => userModel));
    }

    /**
     * This function returns the selected UserModel
     *
     * @returns The selected UserModel
     * @throws Error if the UserModel is not selected yet or the service is not initialized
     */
    public getSelectedUserModel(): UserModel {
        if (!this.isInitialized) {
            throw new Error(
                `UserMasterService: getSelectedUserModel: Trying to call UserMasterService before initialization!`,
            );
        }

        if (!this.selectedUserModel) {
            // Unfortunately the Error class in JavaScript can not be extended easily, so we create a new Error object
            // with a custom message here instead of creating and using a specialised custom error class.
            throw new Error(UserMasterService.USERMODEL_NOT_SELECTED_MESSAGE);
        }

        return this.selectedUserModel;
    }

    public getUserRole(): Observable<string> {
        if (!this.userRole) {
            return this.localStorageAppStateService.getUserRole().pipe(
                tap((userRole) => {
                    this.userRole = userRole;
                }),
            );
        }

        return of(this.userRole);
    }

    public getSelectedUser(): User {
        const userModel = this.getSelectedUserModel();

        return {
            frontId: userModel.getFrontId(),
            country: userModel.getCountry(),
            salesOrg: userModel.getSalesOrg(),
            plant: userModel.getPlant(),
            appId: userModel.getAppId(),
            userRole: userModel.getUserRole(),
            description: userModel.getDescription(),
            emailId: userModel.getEmailId(),
            personnelArea: userModel.getPersonnelArea(),
            personnelSubArea: userModel.getPersonnelSubArea(),
        };
    }

    public getSelectedUserCountry(): string {
        return this.getSelectedUserModel().getCountry();
    }

    public setSalesOrgAndPlantForModel(model: CompositeModel): void {
        const selectedUserModel = this.getSelectedUserModel();
        // adding variable is necessary because of tslint
        model.set(new TextModel('SalesOrg', selectedUserModel.getSalesOrg()));
        model.set(new TextModel('Plant', selectedUserModel.getPlant()));
    }

    public setSalesOrgAndPlantForUrl(query: string): string {
        const selectedUserModel = this.getSelectedUserModel();
        return this.uriUtilities.appendUriFilters(query, selectedUserModel);
    }

    // Determine if the user is external or internal. This is important to evaluate, whether the Pace App or the PaceOnline App is being used.
    public isPaceOnlineUser(): boolean {
        if (!this.isInitialized) {
            return true;
        }
        const frontId = this.selectedUserModel?.getFrontId();
        return !(frontId && frontId[0] === 'Z');
    }

    public getSharpServiceText(): SharpServiceNames {
        if (!this.isInitialized) {
            throw new Error(
                `UserMasterService: getSharpServiceText: Trying to call UserMasterService before initialization!`,
            );
        }
        if (this.clientManager.isPaceFormsFI()) {
            return SharpServiceNames.PACE_FORMS_FI;
        } else if (this.clientManager.isNgmP58()) {
            if (this.isPaceOnlineUser()) {
                return SharpServiceNames.PACE_ONLINE;
            }
            return SharpServiceNames.PACE_PWA;
        } else {
            // if no match occurs return PACE text as default
            return this.defaultSharpServiceText;
        }
    }

    private setUserModel(): void {
        // restore locally stored selectedUserModel (if possible)
        this.localStorageAppStateService
            .getUserData()
            .pipe(
                tap((selectedUserModel) => {
                    this.selectedUserModel = selectedUserModel;
                    // and set the country
                    if (this.selectedUserModel) {
                        this.clientManager.userCountry = this.selectedUserModel.getCountry();
                    }
                }),
                take(1),
                catchError((error) => {
                    this.log.error(this.constructor.name, 'failed to set user model', error);
                    return of(undefined);
                }),
            )
            .subscribe();
    }

    private getUserMasterData(): Observable<UserModel[]> {
        return this.onlineOnlyService.get<ODataUserModel>(this.url).pipe(
            map((userModels: ODataUserModel[]) => {
                if (userModels.length === 0) {
                    throw new Error(
                        `UserMasterService: getUserMasterData: Could not load sales org/plant information!`,
                    );
                }
                return userModels.map((userData) => UserModel.fromOData(userData, this.environmentService.appId));
            }),
        );
    }
}
